/**
 * Motion Max Media Awards — Centralized Media Configuration
 * 
 * All image sources are defined here so admins can replace them
 * via Supabase Storage or a CMS without touching component code.
 * 
 * To integrate with Supabase Storage:
 *   Replace any URL string with: supabase.storage.from('media').getPublicUrl('path/to/image').data.publicUrl
 * 
 * To integrate with a CMS (e.g., Strapi, Contentful):
 *   Fetch the media collection from the CMS API and override these defaults.
 */

export interface MediaAsset {
  src: string;
  alt: string;
  width?: number;
  height?: number;
}

export interface CategoryImage extends MediaAsset {
  id: string;
  name: string;
}

export interface WinnerAsset {
  id: string;
  name: string;
  category: string;
  title: string;
  award: string;
  city: string;
  year: string;
  avatar: string;
  color: string;
  profileImage: MediaAsset;
  projectThumbnail: MediaAsset;
  awardBadge: string; // emoji or icon key
}

export interface SponsorAsset {
  id: string;
  name: string;
  tier: string;
  initials: string;
  logo: MediaAsset;
}

export interface GalleryImage extends MediaAsset {
  id: string;
  caption: string;
  category: 'ceremony' | 'filmmaker' | 'camera' | 'trophy' | 'audience' | 'creator';
}

// ─── HERO IMAGES ────────────────────────────────────────────────────────────
export const HERO_IMAGES: MediaAsset[] = [
{
  src: "https://images.unsplash.com/photo-1728022038090-8ab88f8339bf",
  alt: 'Cinematic film production with camera crew on set',
  width: 1920,
  height: 1080
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1370b5244-1772055141901.png",
  alt: 'Film director reviewing footage on monitor during production',
  width: 1920,
  height: 1080
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ee2a8bee-1772193352417.png",
  alt: 'Professional cinema camera on film set with dramatic lighting',
  width: 1920,
  height: 1080
}];


export const HERO_BACKGROUND: MediaAsset = {
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_12fda46d0-1780654060515.png",
  alt: 'Cinematic film production background',
  width: 1920,
  height: 1080
};

// ─── CATEGORY IMAGES ────────────────────────────────────────────────────────
export const CATEGORY_IMAGES: Record<string, MediaAsset> = {
  'short-film': {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1986ad059-1772151103551.png",
    alt: 'Short film production with director and camera',
    width: 600,
    height: 400
  },
  'documentary': {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1fec3f30d-1772061242251.png",
    alt: 'Documentary filmmaker with camera in natural setting',
    width: 600,
    height: 400
  },
  'music-video': {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1c10e3038-1772432660496.png",
    alt: 'Music video production with colorful stage lighting',
    width: 600,
    height: 400
  },
  'web-series': {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_19a82239d-1780654061578.png",
    alt: 'Web series production with multiple cameras and crew',
    width: 600,
    height: 400
  },
  'reels': {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_187ecb4aa-1773072403284.png",
    alt: 'Content creator filming social media reels with smartphone',
    width: 600,
    height: 400
  },
  'photo-essay': {
    src: "https://images.unsplash.com/photo-1668623387424-12a9a983b1fa",
    alt: 'Professional photographer with camera capturing moments',
    width: 600,
    height: 400
  },
  'branded': {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1a2445d13-1772387255727.png",
    alt: 'Branded content production with professional lighting setup',
    width: 600,
    height: 400
  },
  'podcast': {
    src: "https://images.unsplash.com/photo-1615458317538-72c46d9359d8",
    alt: 'Audio podcast recording studio with microphone and headphones',
    width: 600,
    height: 400
  },
  'cinematography': {
    src: "https://images.unsplash.com/photo-1662602410808-915807b224c1",
    alt: 'Cinematographer operating professional cinema camera',
    width: 600,
    height: 400
  },
  'editing': {
    src: "https://images.unsplash.com/photo-1605894705821-c9586975e412",
    alt: 'Video editor working on post-production at workstation',
    width: 600,
    height: 400
  }
};

// ─── WINNERS GALLERY ────────────────────────────────────────────────────────
export const WINNERS_DATA: WinnerAsset[] = [
{
  id: 'winner-arjun',
  name: 'Arjun Krishnamurthy',
  category: 'Short Film',
  title: 'Echoes of Silence',
  award: 'Gold Award — Best Short Film',
  city: 'Chennai',
  year: '2025',
  avatar: 'AK',
  color: 'from-amber-900/40 to-transparent',
  profileImage: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_16831e89e-1772824395744.png",
    alt: 'Arjun Krishnamurthy, Gold Award winner for Best Short Film',
    width: 200,
    height: 200
  },
  projectThumbnail: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_19e3706ef-1780654061113.png",
    alt: 'Echoes of Silence - award-winning short film thumbnail',
    width: 400,
    height: 225
  },
  awardBadge: '🥇'
},
{
  id: 'winner-priya',
  name: 'Priya Venkataraman',
  category: 'Documentary',
  title: 'The Salt Farmers',
  award: 'Silver Award — Best Documentary',
  city: 'Coimbatore',
  year: '2025',
  avatar: 'PV',
  color: 'from-stone-800/50 to-transparent',
  profileImage: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1c54accd3-1767606997318.png",
    alt: 'Priya Venkataraman, Silver Award winner for Best Documentary',
    width: 200,
    height: 200
  },
  projectThumbnail: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1e68ab0ac-1780654062283.png",
    alt: 'The Salt Farmers - award-winning documentary thumbnail',
    width: 400,
    height: 225
  },
  awardBadge: '🥈'
},
{
  id: 'winner-rahul',
  name: 'Rahul Subramaniam',
  category: 'Music Video',
  title: 'Neon Madurai',
  award: 'Gold Award — Best Music Video',
  city: 'Madurai',
  year: '2025',
  avatar: 'RS',
  color: 'from-yellow-900/40 to-transparent',
  profileImage: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_180a31e00-1780654061186.png",
    alt: 'Rahul Subramaniam, Gold Award winner for Best Music Video',
    width: 200,
    height: 200
  },
  projectThumbnail: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_131627785-1780654060627.png",
    alt: 'Neon Madurai - award-winning music video thumbnail',
    width: 400,
    height: 225
  },
  awardBadge: '🥇'
},
{
  id: 'winner-meera',
  name: 'Meera Iyer',
  category: 'Social Reels',
  title: 'Street Portraits',
  award: 'Platinum — Best Reel Series',
  city: 'Bangalore',
  year: '2025',
  avatar: 'MI',
  color: 'from-amber-900/30 to-transparent',
  profileImage: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_10b8ab9db-1780654070570.png",
    alt: 'Meera Iyer, Platinum Award winner for Best Reel Series',
    width: 200,
    height: 200
  },
  projectThumbnail: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_13d3e6e7b-1780654061562.png",
    alt: 'Street Portraits - award-winning reel series thumbnail',
    width: 400,
    height: 225
  },
  awardBadge: '🏆'
}];


// ─── SPONSOR LOGOS ───────────────────────────────────────────────────────────
export const SPONSORS_DATA: SponsorAsset[] = [
{
  id: 'sp-1',
  name: 'CinemaScope India',
  tier: 'Platinum',
  initials: 'CS',
  logo: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8d83637-1780654060058.png",
    alt: 'CinemaScope India - Platinum Sponsor logo',
    width: 120,
    height: 60
  }
},
{
  id: 'sp-2',
  name: 'Vizag Film Council',
  tier: 'Gold',
  initials: 'VFC',
  logo: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_18febbb3e-1780654061087.png",
    alt: 'Vizag Film Council - Gold Sponsor logo',
    width: 120,
    height: 60
  }
},
{
  id: 'sp-3',
  name: 'MediaVault Pro',
  tier: 'Gold',
  initials: 'MVP',
  logo: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_12f50d3d0-1780654061014.png",
    alt: 'MediaVault Pro - Gold Sponsor logo',
    width: 120,
    height: 60
  }
},
{
  id: 'sp-4',
  name: 'Lens & Light Co.',
  tier: 'Silver',
  initials: 'LL',
  logo: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_17de247bd-1780654060253.png",
    alt: 'Lens & Light Co. - Silver Sponsor logo',
    width: 120,
    height: 60
  }
},
{
  id: 'sp-5',
  name: 'StoryForge Studios',
  tier: 'Silver',
  initials: 'SF',
  logo: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_131283fa6-1780654060271.png",
    alt: 'StoryForge Studios - Silver Sponsor logo',
    width: 120,
    height: 60
  }
},
{
  id: 'sp-6',
  name: 'Reel India Network',
  tier: 'Silver',
  initials: 'RIN',
  logo: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_113758861-1780654061069.png",
    alt: 'Reel India Network - Silver Sponsor logo',
    width: 120,
    height: 60
  }
}];


// ─── AWARDS GALLERY ──────────────────────────────────────────────────────────
export const GALLERY_IMAGES: GalleryImage[] = [
{
  id: 'gal-1',
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_168c187a4-1772259798776.png",
  alt: 'Grand awards ceremony with audience and stage lighting',
  caption: 'Grand Ceremony 2025',
  category: 'ceremony',
  width: 800,
  height: 600
},
{
  id: 'gal-2',
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_19b4b25b2-1773109519520.png",
  alt: 'Filmmaker on set with professional cinema camera',
  caption: 'Behind the Lens',
  category: 'filmmaker',
  width: 600,
  height: 800
},
{
  id: 'gal-3',
  src: "https://images.unsplash.com/photo-1597511858759-8ee3767d9ad4",
  alt: 'Professional cinema camera with anamorphic lens',
  caption: 'Tools of the Trade',
  category: 'camera',
  width: 800,
  height: 600
},
{
  id: 'gal-4',
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_11c07a066-1767885609073.png",
  alt: 'Golden trophy award on display at ceremony',
  caption: 'The Golden Trophy',
  category: 'trophy',
  width: 600,
  height: 800
},
{
  id: 'gal-5',
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1905ffe05-1772259145049.png",
  alt: 'Enthusiastic audience at awards ceremony',
  caption: 'The Audience',
  category: 'audience',
  width: 800,
  height: 600
},
{
  id: 'gal-6',
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_11f551d1b-1772092792528.png",
  alt: 'Content creator filming with smartphone and ring light',
  caption: 'New Age Creator',
  category: 'creator',
  width: 600,
  height: 800
},
{
  id: 'gal-7',
  src: "https://images.unsplash.com/photo-1569016832321-084c128adeb8",
  alt: 'Documentary filmmaker capturing real-life moments',
  caption: 'Documentary Moments',
  category: 'filmmaker',
  width: 800,
  height: 600
},
{
  id: 'gal-8',
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1c13ce962-1768676366126.png",
  alt: 'Award winner receiving trophy on stage',
  caption: 'Moment of Glory',
  category: 'ceremony',
  width: 600,
  height: 800
},
{
  id: 'gal-9',
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_19b4b25b2-1773109519520.png",
  alt: 'Cinematographer with professional camera on film set',
  caption: 'Cinematography in Action',
  category: 'camera',
  width: 800,
  height: 600
}];


// ─── ABOUT SECTION IMAGES ────────────────────────────────────────────────────
export const ABOUT_IMAGES = {
  main: {
    src: "https://images.unsplash.com/photo-1701058661872-3ee9bdc9d28e",
    alt: 'Film crew working on a cinematic production with professional equipment',
    width: 900,
    height: 600
  },
  secondary: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ca423621-1772253554533.png",
    alt: 'Award ceremony stage with dramatic lighting and trophies',
    width: 600,
    height: 400
  }
};

// ─── CONTACT SECTION IMAGES ──────────────────────────────────────────────────
export const CONTACT_IMAGES = {
  office: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1621fe471-1774177507671.png",
    alt: 'Modern premium office space with city view',
    width: 800,
    height: 600
  }
};

// ─── DASHBOARD ASSETS ────────────────────────────────────────────────────────
export const DASHBOARD_ASSETS = {
  emptySubmission: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_11ee55e19-1780654060261.png",
    alt: 'Empty state illustration for video submission',
    width: 400,
    height: 300
  },
  uploadIllustration: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_18be77fcf-1766507337215.png",
    alt: 'Upload video illustration',
    width: 400,
    height: 300
  },
  certificatePreview: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8077d99-1771738588425.png",
    alt: 'Certificate preview placeholder',
    width: 400,
    height: 300
  },
  achievementBadge: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_138106e84-1780654061103.png",
    alt: 'Achievement badge for award winner',
    width: 200,
    height: 200
  }
};

// ─── AUTH PAGE IMAGES ────────────────────────────────────────────────────────
export const AUTH_IMAGES = {
  login: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_17721c655-1772309810848.png",
    alt: 'Cinematic film production with dramatic lighting — login page visual',
    width: 900,
    height: 1200
  },
  register: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1289e7450-1768854130499.png",
    alt: 'Film crew on set with cameras and equipment — registration page visual',
    width: 900,
    height: 1200
  }
};

// ─── ADMIN PANEL ASSETS ──────────────────────────────────────────────────────
export const ADMIN_ASSETS = {
  dashboardIllustration: {
    src: "https://images.unsplash.com/photo-1516383274235-5f42d6c6426d",
    alt: 'Admin dashboard analytics illustration',
    width: 600,
    height: 400
  },
  emptyState: {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1f88daf98-1772222906815.png",
    alt: 'Empty state illustration for admin panel',
    width: 400,
    height: 300
  }
};